import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Box,
  TextField,
  Typography,
  FormHelperText,
  Grid,
} from "@mui/material";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, put } from "../../../utils/api";
import { textError } from "../../../utils/validationMessage";
import {
  annualPolicyYesFieldErrors,
  getEffectiveDateMinMax,
  hasUploadedDocument,
  isAnnualPolicyUploadDisabled,
  shouldShowAnnualPolicyRevised,
} from "../../../utils/annualPolicyFormHelpers";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import WestIcon from "@mui/icons-material/West";

const SOCIAL_PRIMARY = "#1C486B";
const socialRadioSx = {
  color: SOCIAL_PRIMARY,
  "&.Mui-checked": { color: SOCIAL_PRIMARY },
};
const backButtonSx = {
  color: SOCIAL_PRIMARY,
  textTransform: "none",
  border: `1px solid ${SOCIAL_PRIMARY}`,
  "&:hover": { borderColor: SOCIAL_PRIMARY },
};
const primaryButtonSx = {
  textTransform: "none",
  backgroundColor: SOCIAL_PRIMARY,
  "&:hover": { backgroundColor: "#163a56" },
};

const NumericInputField = ({
  label,
  name,
  value,
  errors,
  onChange,
  placeholder,
}) => (
  <div style={{ marginBottom: "1rem", marginTop: "1rem" }}>
    <p
      className={styles.form_heading}
      style={{ fontWeight: "bold", marginTop: "1rem" }}
    >
      {label}
    </p>
    <TextField
      name={name}
      value={value}
      onChange={onChange}
      variant="outlined"
      size="small"
      sx={{ pl: 2, width: "50%" }}
      placeholder={placeholder}
      error={errors?.[name]}
    />
    {errors?.[name] && (
      <FormHelperText
        sx={{ color: "#D34040", marginLeft: "1rem", fontSize: "14px" }}
      >
        {errors?.[name]}
      </FormHelperText>
    )}
  </div>
);

const BooleanInputField = ({ label, name, value, onChange }) => (
  <FormControl component="fieldset" style={{  }}>
    <p
      className={styles.form_heading}
      style={{ fontWeight: "bold", marginTop:"1rem" }}
    >
      {label}
    </p>
    <RadioGroup
      row
      name={name}
      value={value?.toString() || "true"}
      onChange={onChange}
      sx={{ pl: 2 }}
    >
      <FormControlLabel
        value="true"
        control={<Radio sx={socialRadioSx} />}
        label="Yes"
      />
      <FormControlLabel
        value="false"
        control={<Radio sx={socialRadioSx} />}
        label="No"
      />
    </RadioGroup>
  </FormControl>
);

const CommunityEngagement = () => {
  const removeCommas = (val) => {
    if (val === null || val === undefined) return "";
    return val.toString().replace(/,/g, "").trim();
  };

  const formatWithCommas = (val) => {
    const normalized = removeCommas(val);
    if (normalized === "") return "";
    if (!/^\d*\.?\d*$/.test(normalized)) return normalized;

    const [integerPart = "", decimalPart] = normalized.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [module, setModule] = useState("monthly");
  const [existingCSRDocUrl, setExistingCSRDocUrl] = useState("");
  const [isCSRDocumentDeleted, setIsCSRDocumentDeleted] = useState(false);
  const [prevCSR, setPrevCSR] = useState({
    documentUrl: "",
    remark: "",
    effectiveDate: "",
  });

  // Monthly formData
  const monthlyFormDataInitial = {
    communityProjectsSponsored: "",
    communityProjectsSponsoredRemark: "",
    totalDonated: { currency: "", amount: "" },
    volunteerHours: "",
    localOrganizationPartnerships: "",
    localOrganizationPartnershipsRemark: "",
    projectImpactQualitative: {
      status: true,
      remark: "",
      documentUrl: "",
    },
    projectImpactQuantitativeNos: {
      status: true,
      remark: "",
      documentUrl: "",
    },
    marginalBeneficiariesPercentage: "",
  };

  // Annual formData
  const annualFormDataInitial = {
    CSR: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
  };

  const [monthlyFormData, setMonthlyFormData] = useState(
    monthlyFormDataInitial,
  );
  const [annualFormData, setAnnualFormData] = useState(annualFormDataInitial);
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  const [previousYearCSRDocumentUrl, setPreviousYearCSRDocumentUrl] =
    useState("");
  const [entryYear, setEntryYear] = useState(null);
  const todayIso = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();
  const maxEffectiveDate =
    Number(entryYear) && Number(entryYear) < currentYear
      ? `${entryYear}-12-31`
      : todayIso;
  const { min: csrEffectiveMin, max: csrEffectiveMax } =
    getEffectiveDateMinMax(entryYear);

  // Get the appropriate formData based on module
  const formData = module === "annual" ? annualFormData : monthlyFormData;
  const setFormData =
    module === "annual" ? setAnnualFormData : setMonthlyFormData;

  const fetchData = useCallback(
    async (id) => {
      try {
        const response = await get(`/social/getSocial?id=${id}`);
        // Handle array response - get first item if array, otherwise use directly
        const responseData = response?.data?.data;
        const socialData = Array.isArray(responseData)
          ? responseData[0]
          : responseData;
        const fetchedData = socialData?.communityEngagement || {};
        const toBoolean = (val, defaultValue = false) => {
          if (typeof val === "boolean") return val;
          if (typeof val === "string") {
            const normalized = val.trim().toLowerCase();
            if (normalized === "true") return true;
            if (normalized === "false") return false;
          }
          return defaultValue;
        };
        const sanitizeDocumentUrl = (value) => {
          if (typeof value !== "string") return value || "";
          const cleaned = value.trim();
          if (!cleaned) return "";
          if (cleaned.toLowerCase() === "no documents available") return "";
          return cleaned;
        };

        // Get module from URL params, API response, or default to monthly
        const urlModule = searchParams.get("entryType");
        const apiModule =
          socialData?.module || socialData?._socialDataRef?.module;
        let finalModule = "monthly";

        if (
          urlModule === "annual" ||
          apiModule === "Annual" ||
          apiModule === "annual"
        ) {
          finalModule = "annual";
        } else if (
          urlModule === "monthly" ||
          apiModule === "Monthly" ||
          apiModule === "monthly"
        ) {
          finalModule = "monthly";
        }

        setModule(finalModule);

        // Separate monthly and annual data
        const monthlyData = {};
        const annualData = {};
        const currentEntryYear =
          Number(socialData?._socialDataRef?.yearToSubmit) ||
          Number(socialData?.yearToSubmit) ||
          Number(socialData?.year) ||
          undefined;
        setEntryYear(currentEntryYear || null);
        const companyId = socialData?.company;
        let prevCSRDocUrl = "";
        let prevCSRRemark = "";
        let prevCSRStatusRemark = "";
        let prevCSREffectiveDate = "";
        let prevCSRStatus;
        let prevCSRIsRevised;
        try {
          const qs = new URLSearchParams();
          if (currentEntryYear) qs.set("year", String(currentEntryYear));
          if (companyId) qs.set("companyId", String(companyId));
          const prevUrl = `/social/totalWorkforce/annualRangeWithPrev${qs.toString() ? `?${qs.toString()}` : ""}`;
          const prevRes = await get(prevUrl);
          setIsFirstEntry(Boolean(prevRes?.data?.data?.isFirstEntry));
          const prevPolicies = prevRes?.data?.data?.policies || {};
          const prevCSRFromPolicies = prevPolicies?.["communityEngagement.CSR"] || {};
          prevCSRDocUrl = sanitizeDocumentUrl(
            prevCSRFromPolicies?.documentUrl || ""
          );
          setPreviousYearCSRDocumentUrl(prevCSRDocUrl);
          prevCSRRemark = prevCSRFromPolicies?.remark || "";
          prevCSRStatusRemark = prevCSRFromPolicies?.statusremark || "";
          prevCSREffectiveDate = prevCSRFromPolicies?.effectiveDate || "";
          prevCSRStatus = prevCSRFromPolicies?.status;
          prevCSRIsRevised = prevCSRFromPolicies?.isRevised;
        } catch (e) {
          // non-blocking
          setIsFirstEntry(false);
          setPreviousYearCSRDocumentUrl("");
        }

        if (fetchedData) {
          // Monthly fields
          const monthlyFields = [
            "communityProjectsSponsored",
            "communityProjectsSponsoredRemark",
            "totalDonated",
            "volunteerHours",
            "localOrganizationPartnerships",
            "localOrganizationPartnershipsRemark",
            "projectImpactQualitative",
            "projectImpactQuantitativeNos",
            "marginalBeneficiariesPercentage",
          ];

          // Annual fields
          const annualFields = ["CSR"];

          Object.keys(fetchedData).forEach((key) => {
            if (monthlyFields.includes(key)) {
              if (
                [
                  "communityProjectsSponsored",
                  "volunteerHours",
                  "localOrganizationPartnerships",
                  "marginalBeneficiariesPercentage",
                ].includes(key)
              ) {
                monthlyData[key] = formatWithCommas(fetchedData[key]);
              } else {
                monthlyData[key] = fetchedData[key];
              }
            } else if (annualFields.includes(key)) {
              annualData[key] = fetchedData[key];
            }
          });

          const initialPrevCSR = {
            documentUrl:
              sanitizeDocumentUrl(fetchedData?.CSR?.documentUrl || "") ||
              prevCSRDocUrl ||
              "",
            remark: fetchedData?.CSR?.remark || prevCSRRemark || "",
            statusremark:
              fetchedData?.CSR?.statusremark || prevCSRStatusRemark || "",
            effectiveDate:
              fetchedData?.CSR?.effectiveDate || prevCSREffectiveDate || "",
          };
          setExistingCSRDocUrl(initialPrevCSR.documentUrl);
          setPrevCSR(initialPrevCSR);
        }

        // Update monthly form data
        setMonthlyFormData((prevState) => {
          const updated = {
            ...prevState,
            ...monthlyData,
          };
          if (updated?.totalDonated) {
            updated.totalDonated = {
              ...prevState.totalDonated,
              ...updated.totalDonated,
              amount: formatWithCommas(updated.totalDonated.amount),
            };
          }
          // Ensure status fields default to true if not present
          if (updated.projectImpactQualitative) {
            updated.projectImpactQualitative = {
              ...prevState.projectImpactQualitative,
              ...updated.projectImpactQualitative,
              status: updated.projectImpactQualitative.status ?? true,
            };
          } else {
            updated.projectImpactQualitative =
              prevState.projectImpactQualitative;
          }
          if (updated.projectImpactQuantitativeNos) {
            updated.projectImpactQuantitativeNos = {
              ...prevState.projectImpactQuantitativeNos,
              ...updated.projectImpactQuantitativeNos,
              status: updated.projectImpactQuantitativeNos.status ?? true,
            };
          } else {
            updated.projectImpactQuantitativeNos =
              prevState.projectImpactQuantitativeNos;
          }
          return updated;
        });

        // Update annual form data
        setAnnualFormData((prevState) => {
          const updated = {
            ...prevState,
            ...annualData,
          };
          // Ensure CSR status defaults to true if not present
          if (updated.CSR) {
            updated.CSR = {
              ...prevState.CSR,
              ...updated.CSR,
              status:
                updated.CSR.status !== undefined
                  ? toBoolean(updated.CSR.status, true)
                  : prevCSRStatus !== undefined
                    ? toBoolean( true)
                    : true,
              isRevised:
                updated.CSR.isRevised !== undefined
                  ? toBoolean(updated.CSR.isRevised, false)
                  : prevCSRIsRevised !== undefined
                    ? toBoolean(prevCSRIsRevised, false)
                    : prevState.CSR.isRevised ?? false,
              documentUrl:
                sanitizeDocumentUrl(updated.CSR.documentUrl || "") ||
                prevCSRDocUrl ||
                sanitizeDocumentUrl(prevState.CSR.documentUrl || "") ||
                "",
              remark:
                updated.CSR.remark ||
                prevCSRRemark ||
                prevState.CSR.remark ||
                "",
              statusremark:
               updated.CSR.statusremark 
                || prevState.CSR.statusremark
                || "",
              effectiveDate:
                updated.CSR.effectiveDate ||
                prevCSREffectiveDate ||
                prevState.CSR.effectiveDate ||
                "",
            };
          } else {
            updated.CSR = prevState.CSR;
          }
          return updated;
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    [searchParams],
  );

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);

  const validation = () => {
    let newErrors = {};
    let hasErrors = false;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;

    const validateRecursive = (data, errorsObj) => {
      Object.entries(data).forEach(([key, value]) => {
        if (
          [
            "communityProjectsSponsoredRemark",
            "localOrganizationPartnershipsRemark",
          ].includes(key)
        ) {
          return;
        }
        if (Array.isArray(value)) {
          let arrayErrors = value
            .map((item, index) => {
              if (typeof item === "object") {
                let nestedErrors = {};
                validateRecursive(item, nestedErrors);
                return Object.keys(nestedErrors).length > 0
                  ? nestedErrors
                  : null;
              } else {
                return isEmpty(item) ? textError : null;
              }
            })
            .filter((error) => error !== null);

          if (arrayErrors.length > 0) {
            errorsObj[key] = arrayErrors;
            hasErrors = true;
          }
        } else if (typeof value === "object" && value !== null) {
          let nestedErrors = {};

          if (
            [
              "projectImpactQuantitativeNos",
              "projectImpactQualitative",
            ].includes(key)
          ) {
            if (value.status) {
              const isDocumentMissing = isEmpty(value.documentUrl);
              const isRemarkMissing = isEmpty(value.remark);

              if (isDocumentMissing && isRemarkMissing) {
                nestedErrors.documentUrl = textError;
                nestedErrors.remark = textError;
                hasErrors = true;
              }
            }
          } else if (key === "CSR") {
            if (value.status) {
              const showRev = shouldShowAnnualPolicyRevised(
                !isFirstEntry,
                previousYearCSRDocumentUrl,
              );
              const polErr = annualPolicyYesFieldErrors(
                value,
                entryYear,
                showRev,
              );
              Object.assign(nestedErrors, polErr);
              if (Object.keys(polErr).length) hasErrors = true;
            }
          } else {
            validateRecursive(value, nestedErrors);
          }

          if (Object.keys(nestedErrors).length > 0) {
            errorsObj[key] = nestedErrors;
            hasErrors = true;
          }
        } else if (isEmpty(value)) {
          errorsObj[key] = textError;
          hasErrors = true;
        }
      });
    };

    // Only validate fields relevant to the current module
    if (module === "annual") {
      // For annual module, only validate CSR
      validateRecursive({ CSR: formData.CSR }, newErrors);

      // Remove errors if CSR status is false
      if (formData.CSR?.status === false) {
        delete newErrors.CSR;
      }
    } else {
      // For monthly module, validate all monthly fields
      validateRecursive(formData, newErrors);

      // Remove errors if status is false
      ["projectImpactQuantitativeNos", "projectImpactQualitative"].forEach(
        (key) => {
          if (formData[key]?.status === false) {
            delete newErrors[key];
          }
        },
      );
    }

    setErrors(newErrors);
    return hasErrors;
  };

  const handleNumberChange = (e, type) => {
    const { name, value } = e.target;
    const normalizedValue = removeCommas(value);
    console.log(type, name, "valuess");
    if (type === "number" && !/^\d*\.?\d*$/.test(normalizedValue)) return;
    if (type === "percentage") {
      // if(/^\d*\.?\d*$/.test(value) && (value < 0 || value > 100)) return;
      if (
        !/^\d*\.?\d*$/.test(normalizedValue) ||
        normalizedValue < 0 ||
        normalizedValue > 100
      )
        return;
    }
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    console.log(value, "values");
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "number" || type === "percentage"
          ? formatWithCommas(normalizedValue)
          : value || "",
    }));
  };

  const handleNestedBooleanChange = (e) => {
    const { name, value } = e.target;
    const [parent, key] = name.split(".");
    const nextStatus = value === "true";

    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: nextStatus,
        ...(parent === "CSR" && key === "isRevised" && nextStatus === false
          ? {
              documentUrl:
                existingCSRDocUrl ||
                prevCSR.documentUrl ||
                prev[parent]?.documentUrl ||
                "",
              remark:
                typeof prevCSR.remark === "string"
                  ? prevCSR.remark
                  : prev[parent]?.remark || "",
              effectiveDate:
                typeof prevCSR.effectiveDate === "string"
                  ? prevCSR.effectiveDate
                  : prev[parent]?.effectiveDate || "",
            }
          : {}),
        ...(parent === "CSR" && key === "isRevised" && nextStatus === true
          ? { documentUrl: "", remark: "", effectiveDate: "" }
          : {}),
        // If user selects "No" for status, make nested fields non-mandatory by clearing them
        // ...(key === "status" && nextStatus === false
        //   ? { remark: "", documentUrl: "", isRevised: false, effectiveDate: "" }
        //   : {}),
      },
    }));

    if (parent === "CSR" && key === "isRevised" && nextStatus === false) {
      setIsCSRDocumentDeleted(false);
    }

    // Clear errors immediately when status is switched to "No"
    if (key === "status" && nextStatus === false) {
      setErrors((prevErr) => {
        const next = { ...(prevErr || {}) };
        // remove the whole nested error object for this section
        if (next[parent]) delete next[parent];
        return next;
      });
    }
  };

  const handleFileChange = (file, policy) => {
    if (policy === "CSR") {
      setIsCSRDocumentDeleted(!hasUploadedDocument(file));
    }

    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        documentUrl: file,
      },
    }));
    if (policy === "CSR" && hasUploadedDocument(file)) {
      setErrors((prev) => {
        const next = { ...prev };
        const cur =
          typeof next.CSR === "object" && next.CSR ? { ...next.CSR } : {};
        delete cur.documentUrl;
        if (Object.keys(cur).length) next.CSR = cur;
        else delete next.CSR;
        return next;
      });
    } else if (policy !== "CSR") {
      setErrors((prev) => ({
        ...prev,
        [policy]: {
          ...prev[policy],
          documentUrl: "",
          remark: "",
        },
      }));
    }
  };

  const handleSubmit = async () => {
    if (validation()) return;

    // Send the appropriate formData based on module
    const dataToSubmit = module === "annual" ? annualFormData : monthlyFormData;

    // Clean up the payload: if CSR status is false, set documentUrl and remark to ""
    let cleanedFormData = {};
    if (module === "annual") {
      cleanedFormData = {
        CSR: {
          ...dataToSubmit.CSR,
          ...(dataToSubmit.CSR.status === false
            ? { documentUrl: "", remark: "", isRevised: false, effectiveDate: "" }
            : { statusremark: "" }),
        },
      };
    } else {
      cleanedFormData = {
        ...dataToSubmit,
        totalDonated: {
          ...dataToSubmit.totalDonated,
          amount: removeCommas(dataToSubmit?.totalDonated?.amount),
        },
        projectImpactQualitative: {
          ...dataToSubmit.projectImpactQualitative,
          ...(dataToSubmit.projectImpactQualitative.status === false
            ? { documentUrl: "", remark: "" }
            : {}),
        },
        projectImpactQuantitativeNos: {
          ...dataToSubmit.projectImpactQuantitativeNos,
          ...(dataToSubmit.projectImpactQuantitativeNos.status === false
            ? { documentUrl: "", remark: "" }
            : {}),
        },
      };
    }

    const payload = {
      communityEngagement: cleanedFormData,
      stepsCompleted: {
        communityEngagement: true,
      },
    };

    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((res) => {
        const entryTypeParam = module === "annual" ? "annual" : "monthly";
        navigate(`/social/data/labour-practices/${id}`);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const displayedCSRPolicyDocument = isCSRDocumentDeleted
    ? formData?.CSR?.documentUrl || ""
    : formData?.CSR?.documentUrl ||
      (formData?.CSR?.isRevised
        ? ""
        : existingCSRDocUrl || prevCSR.documentUrl || "");

  let list = ["USD", "INR", "YEN", "EURO", "AUD"];
  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div> */}
      {/* <div
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#001632",
          marginBottom: "1rem",
        }}
      >
        Add New Entry
      </div> */}
      <p className={styles.form_headingStrongUpper}>C. COMMUNITY ENGAGEMENT</p>

      {/* Module Selection */}

      {/* Conditionally render based on module type */}
      {module === "annual" ? (
        // Annual Module - CSR Policy
        <>
          <BooleanInputField
            label="1. CSR (Corporate Social Responsibility) Policy:"
            name="CSR.status"
            value={formData?.CSR?.status ?? true}
            onChange={handleNestedBooleanChange}
          />
          {(formData.CSR?.status ?? true) ? (
            <Box sx={{ ml: "1.1rem" }}>
              {shouldShowAnnualPolicyRevised(
                !isFirstEntry,
                previousYearCSRDocumentUrl,
              ) && (
                <BooleanInputField
                  label="Is the Policy revised?"
                  name="CSR.isRevised"
                  value={formData?.CSR?.isRevised ?? false}
                  onChange={handleNestedBooleanChange}
                />
              )}
              <Typography sx={{ fontWeight: "bold", ml: "0.1rem", }}>
                Upload evidence (for Policy):
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  ml: "0.1rem",
                  
                }}
              >
                <Box
                  sx={{
                    width: hasUploadedDocument(displayedCSRPolicyDocument)
                      ? "233px"
                      : "100px",
                  }}
                >
                  <FileUpload
                    setDocumentUrl={(file) => handleFileChange(file, "CSR")}
                    filesList={displayedCSRPolicyDocument}
                    isDisabled={isAnnualPolicyUploadDisabled(
                      !isFirstEntry,
                      previousYearCSRDocumentUrl,
                    )}
                  />
                  {errors?.CSR?.documentUrl ? (
                    <FormHelperText
                      sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                    >
                      {errors?.CSR?.documentUrl}
                    </FormHelperText>
                  ) : null}
                </Box>
                <Box
                  sx={{
                    width: "30%",
                    mt: hasUploadedDocument(displayedCSRPolicyDocument)
                      ? "15px"
                      : "10px",
                      ml:hasUploadedDocument(displayedCSRPolicyDocument) ? "0" : "1.1rem",
                  }}
                >
                  <TextField
                    fullWidth
                    type="Enter Additional Text"
                    size="small"
                    variant="outlined"
                    name="CSR.remark"
                    value={formData.CSR.remark || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        CSR: {
                          ...prev.CSR,
                          remark: v,
                        },
                      }));
                      if (String(v || "").trim()) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          const cur =
                            typeof next.CSR === "object" && next.CSR
                              ? { ...next.CSR }
                              : {};
                          delete cur.remark;
                          if (Object.keys(cur).length) next.CSR = cur;
                          else delete next.CSR;
                          return next;
                        });
                      }
                    }}
                    placeholder="Enter text for additional details"
                    error={!!errors?.CSR?.remark}
                    multiline
                    minRows={1}
                    InputProps={{ sx: { padding: "9px 12px" } }}
                  />
                  <FormHelperText
                    sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                  >
                    {errors?.CSR?.remark || " "}
                  </FormHelperText>
                </Box>
              </Box>
              <Typography sx={{ fontWeight: "bold", ml: "0.1rem",mb:"12px" }}>
                Effective date (for Policy):
              </Typography>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                name="CSR.effectiveDate"
                value={formData.CSR.effectiveDate || ""}
                inputProps={{
                  min: "2021-01-01",
                  ...(csrEffectiveMax
                    ? { max: csrEffectiveMax }
                    : { max: maxEffectiveDate }),
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    CSR: {
                      ...prev.CSR,
                      effectiveDate: v,
                    },
                  }));
                  setErrors((prev) => {
                    const next = { ...prev };
                    const cur =
                      typeof next.CSR === "object" && next.CSR
                        ? { ...next.CSR }
                        : {};
                    delete cur.effectiveDate;
                    if (Object.keys(cur).length) next.CSR = cur;
                    else delete next.CSR;
                    return next;
                  });
                }}
                error={!!errors?.CSR?.effectiveDate}
                sx={{
                  width: "30%",
                  ml: "0.1rem",
                  mb: "0.25rem",
                  "& .MuiInputBase-root": { height: 40 },
                }}
              />
              <FormHelperText
                sx={{
                  color: "#D34040",
                  fontSize: "14px",
                  marginLeft: "0.1rem",
                  width: "30%",
                }}
              >
                {errors?.CSR?.effectiveDate}
              </FormHelperText>
            </Box>
          ) : (
            <div
              style={{
                marginTop: "0.5rem",
                marginBottom: "1rem",
                marginLeft: "1rem",
              }}
            >
              {/* <p style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>Remark</p> */}
              <TextField
                type="text"
                size="small"
                variant="outlined"
                name="CSR.statusremark"
                value={formData.CSR.statusremark || ""}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    CSR: {
                      ...prev.CSR,
                      statusremark: e.target.value,
                    },
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    CSR: {
                      ...prev.CSR,
                      statusremark: "",
                    },
                  }));
                }}
                sx={{ width: "40%" }}
                placeholder="Enter Additional Text"
                error={!!errors?.CSR?.["statusremark"]}
              />
              {errors?.CSR?.["statusremark"] && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    fontSize: "14px",
                  }}
                >
                  {errors?.CSR?.["statusremark"]}
                </FormHelperText>
              )}
            </div>
          )}
        </>
      ) : (
        // Monthly Module - All other fields
        <>
          <NumericInputField
            label="1. Number of Community Projects Sponsored (1 project is 1 site and 1 topic):"
            name="communityProjectsSponsored"
            value={formData.communityProjectsSponsored}
            onChange={(e) => handleNumberChange(e, "number")}
            placeholder="Enter Number"
            errors={errors}
          />
          <p
            style={{
              fontWeight: "bold",
              marginLeft: "1rem",
              marginBottom: "0px",
              marginTop: "1rem",
            }}
          >
            Additional Details
          </p>
          <TextField
            name={"communityProjectsSponsoredRemark"}
            value={formData.communityProjectsSponsoredRemark}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                communityProjectsSponsoredRemark: e.target.value,
              }));
            }}
            variant="outlined"
            // type="number"
            size="small"
            sx={{ width: "49.2%", mb: 2, ml: 1.9 }}
            placeholder={"Enter Text"}
          />
          <div
            style={{ width: "100%", marginBottom: "1rem", marginTop: "1rem" }}
          >
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              2. Total Amount Donated to Community Projects:
            </Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                width: "100%",
                paddingLeft: "1rem",
              }}
            >
              <div style={{ width: "50%", marginTop: "0.5rem" }}>
                <p>Currency</p>
                <Select
                  name="currency"
                  value={formData.totalDonated.currency || "Tap to select"}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      totalDonated: {
                        ...prev.totalDonated,
                        currency: e.target.value,
                      },
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      totalDonated: {
                        ...prev.totalDonated,
                        currency: "",
                      },
                    }));
                  }}
                  size="small"
                  sx={{ width: "100%" }}
                  error={!!errors?.totalDonated?.["currency"]}
                >
                  <MenuItem value="Tap to select" disabled>
                    Tap to select
                  </MenuItem>

                  {list.map((item, index) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {errors?.totalDonated?.["currency"] && (
                  <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                    {errors?.totalDonated?.["currency"]}{" "}
                    {/* Customize the error text appearance */}
                  </FormHelperText>
                )}
              </div>
              <div style={{ width: "50%", marginTop: "0.5rem" }}>
                <p>Amount</p>
                <TextField
                  name={"amount"}
                  value={formData.totalDonated.amount}
                  onChange={(e) => {
                    const normalizedValue = removeCommas(e.target.value);
                    if (!/^\d*\.?\d*$/.test(normalizedValue)) return;
                    setFormData((prev) => ({
                      ...prev,
                      totalDonated: {
                        ...prev.totalDonated,
                        amount: formatWithCommas(normalizedValue),
                      },
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      totalDonated: {
                        ...prev.totalDonated,
                        amount: "",
                      },
                    }));
                  }}
                  variant="outlined"
                  // type="number"
                  size="small"
                  fullWidth
                  placeholder={"Enter Number"}
                  error={!!errors?.totalDonated?.["amount"]}
                />
                {errors?.totalDonated?.["amount"] && (
                  <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                    {errors?.totalDonated?.["amount"]}{" "}
                    {/* Customize the error text appearance */}
                  </FormHelperText>
                )}
              </div>
            </div>
          </div>

          <NumericInputField
            label="3. Employee Volunteer Hours:"
            name="volunteerHours"
            value={formData.volunteerHours}
            onChange={(e) => handleNumberChange(e, "number")}
            placeholder="Enter Number"
            errors={errors}
          />

          <NumericInputField
            label="4. Number of Partnerships with Local Organizations:"
            name="localOrganizationPartnerships"
            value={formData.localOrganizationPartnerships}
            onChange={(e) => handleNumberChange(e, "number")}
            placeholder="Enter Number"
            errors={errors}
          />
          <p
            style={{
              fontWeight: "bold",
              marginLeft: "1rem",
              marginBottom: "0px",
            }}
          >
            Additional Details
          </p>
          <TextField
            name={"localOrganizationPartnershipsRemark"}
            value={formData.localOrganizationPartnershipsRemark}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                localOrganizationPartnershipsRemark: e.target.value,
              }));
            }}
            variant="outlined"
            // type="number"
            size="small"
            sx={{ width: "49.2%", mb: 2, ml: 1.9 }}
            placeholder={"Enter Text"}
          />
          <Box>
            <BooleanInputField
              label="5. Impact of Community Projects (qualitative assessment)"
              name="projectImpactQualitative.status"
              value={formData.projectImpactQualitative?.status ?? true}
              onChange={handleNestedBooleanChange}
            />
          </Box>

          {(formData.projectImpactQualitative?.status ?? true) && (
            <div style={{ paddingLeft: "1rem", width: "100%" }}>
              <p>Provide information or upload evidence</p>
              <div
                style={{
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "9rem",
                  }}
                >
                  <span style={{ marginTop: "-0.8rem"}}>
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "projectImpactQualitative")
                      }
                      filesList={formData.projectImpactQualitative.documentUrl}
                      isDisabled={true}
                    />
                  </span>
                  {errors?.projectImpactQualitative?.["documentUrl"] && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        // marginLeft: "1rem",
                        fontSize: "14px",
                      }}
                    >
                      {errors?.projectImpactQualitative?.["documentUrl"]}{" "}
                      {/* Customize the error text appearance */}
                    </FormHelperText>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    
                    marginLeft:formData.projectImpactQualitative.documentUrl?"5.1rem":"0rem"
                  }}
                >
                  <TextField
                    type="Enter Additional Text"
                    size="small"
                    variant="outlined"
                    name="projectImpactQualitative.remark"
                    value={formData.projectImpactQualitative.remark}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        projectImpactQualitative: {
                          ...prev.projectImpactQualitative,
                          remark: e.target.value,
                        },
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        ["projectImpactQualitative"]: {
                          ...prev["projectImpactQualitative"],
                          remark: "",
                          documentUrl: "",
                        },
                      }));
                    }}
                    sx={{ width: "100%" }}
                    placeholder="Enter Additional Text"
                    error={!!errors?.projectImpactQualitative?.["remark"]}
                  />
                  {errors?.projectImpactQualitative?.["remark"] && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                      }}
                    >
                      {errors?.projectImpactQualitative?.["remark"]}{" "}
                      {/* Customize the error text appearance */}
                    </FormHelperText>
                  )}
                </div>
                
              </div>
            </div>
          )}
          {/* <br /> */}

          <BooleanInputField
            label="6. Impact of Community Projects (quantitative assessment - nos.):"
            name="projectImpactQuantitativeNos.status"
            value={formData.projectImpactQuantitativeNos?.status ?? true}
            onChange={handleNestedBooleanChange}
          />

          {(formData.projectImpactQuantitativeNos?.status ?? true) && (
            <div style={{paddingLeft: "1rem", width: "100%"}}>
              <p>Provide information or upload evidence</p>
              <div
                style={{
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                  <div style={{ display: "flex", flexDirection: "column" ,width: "9rem"}}>
                  <span style={{ marginTop: "-0.8rem" }}>
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "projectImpactQuantitativeNos")
                      }
                      filesList={
                        formData.projectImpactQuantitativeNos.documentUrl
                      }
                      isDisabled={true}
                    />
                  </span>
                  {errors?.projectImpactQuantitativeNos?.["documentUrl"] && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        // marginLeft: "1rem",
                        fontSize: "14px",
                      }}
                    >
                      {errors?.projectImpactQuantitativeNos?.["documentUrl"]}{" "}
                      {/* Customize the error text appearance */}
                    </FormHelperText>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column",marginLeft:formData.projectImpactQuantitativeNos.documentUrl?"5.1rem":"0rem" }}>
                  <TextField
                    type="Enter Additional Text"
                    size="small"
                    variant="outlined"
                    name="projectImpactQuantitativeNos.remark"
                    value={formData.projectImpactQuantitativeNos.remark}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        projectImpactQuantitativeNos: {
                          ...prev.projectImpactQuantitativeNos,
                          remark: e.target.value,
                        },
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        ["projectImpactQuantitativeNos"]: {
                          ...prev["projectImpactQuantitativeNos"],
                          remark: "",
                          documentUrl: "",
                        },
                      }));
                    }}
                    sx={{ width: "100%" }}
                    placeholder="Enter Additional Text"
                    error={!!errors?.projectImpactQuantitativeNos?.["remark"]}
                  />
                  {errors?.projectImpactQuantitativeNos?.["remark"] && (
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                      }}
                    >
                      {errors?.projectImpactQuantitativeNos?.["remark"]}{" "}
                      {/* Customize the error text appearance */}
                    </FormHelperText>
                  )}
                </div>
              
              </div>
            </div>
          )}

          <NumericInputField
            label="7. Percentage of marginal / vulnerable beneficiary"
            name="marginalBeneficiariesPercentage"
            value={formData.marginalBeneficiariesPercentage}
            onChange={(e) => handleNumberChange(e, "percentage")}
            placeholder="Enter Percentage"
            errors={errors}
          />
        </>
      )}

      <Box
        sx={{
          width: "100%",
          mt: "10%",
          mb: 5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          sx={backButtonSx}
          onClick={() => navigate(`/social/data/diversity-and-inclusion/${id}`)}
        >
          <WestIcon sx={{ mr: 2 }} /> Back
        </Button>
        <Button
          variant="contained"
          className={styles.submit_button}
          onClick={handleSubmit}
          sx={primaryButtonSx}
        >
          Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
        </Button>
      </Box>
    </MainDashboardLayout>
  );
};

export default CommunityEngagement;
